import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered } from "lucide-react";
import { escapeHtml, sanitizeRichHtml } from "@/lib/rich-line-text";
import { cn } from "@/lib/utils";

/** The editor's starting HTML: the rich copy, or the plain words (line breaks kept). */
export function initialRichHtml(html: string | null | undefined, plain: string | null | undefined): string {
    if (html) return sanitizeRichHtml(html);
    return sanitizeRichHtml(escapeHtml(String(plain ?? "")).replace(/\r?\n/g, "<br>"));
}

const TOOLS = [
    { cmd: "bold", label: "Bold", Icon: Bold },
    { cmd: "italic", label: "Italic", Icon: Italic },
    { cmd: "insertUnorderedList", label: "Bullets", Icon: List },
    { cmd: "insertOrderedList", label: "Numbers", Icon: ListOrdered },
] as const;

/**
 * Bold, italic, bullets, numbers — and nothing else (Faster Estimates,
 * decision 5). Pasted text keeps only those four; the server sanitizes again on
 * save. ⌘B / ⌘I work as everywhere else.
 */
export function RichLineEditor({
    initialHtml,
    onChange,
    onSubmit,
    onCancel,
    autoFocus,
    className,
    label = "Line description",
}: {
    initialHtml: string;
    onChange: (html: string) => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    autoFocus?: boolean;
    className?: string;
    label?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    // Seed once: a contentEditable that React re-renders loses the caret.
    useEffect(() => {
        if (ref.current) ref.current.innerHTML = initialHtml;
        if (autoFocus && ref.current) {
            ref.current.focus();
            const sel = window.getSelection();
            if (sel) {
                sel.selectAllChildren(ref.current);
                sel.collapseToEnd();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const emit = () => onChange(sanitizeRichHtml(ref.current?.innerHTML ?? ""));
    return (
        <div
            className={cn(
                "overflow-hidden rounded-[10px] border bg-card transition-shadow focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/25",
                className,
            )}
        >
            <div role="toolbar" aria-label="Text style" className="flex gap-0.5 border-b bg-muted/60 p-1">
                {TOOLS.map(({ cmd, label: l, Icon }, k) => (
                    <span key={cmd} className="contents">
                        {k === 2 && <span className="mx-[3px] my-1 w-px bg-border" />}
                        <button
                            type="button"
                            title={l}
                            aria-label={l}
                            className="inline-flex h-7 w-[30px] items-center justify-center rounded-md text-foreground hover:bg-card"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                ref.current?.focus();
                                try {
                                    document.execCommand(cmd, false);
                                } catch {
                                    /* old browser: the words still save */
                                }
                                emit();
                            }}
                        >
                            <Icon className="h-4 w-4" />
                        </button>
                    </span>
                ))}
            </div>
            <div
                ref={ref}
                role="textbox"
                aria-multiline="true"
                aria-label={label}
                contentEditable
                suppressContentEditableWarning
                data-testid="rich-line-editor"
                className="min-h-[72px] px-3 py-2.5 text-sm outline-none [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5"
                onInput={emit}
                onBlur={emit}
                onPaste={(e) => {
                    // Paste keeps only the four styles — never a stranger's colors or links.
                    const html = e.clipboardData.getData("text/html");
                    const text = e.clipboardData.getData("text/plain");
                    e.preventDefault();
                    const clean = html ? sanitizeRichHtml(html) : escapeHtml(text).replace(/\r?\n/g, "<br>");
                    try {
                        document.execCommand("insertHTML", false, clean);
                    } catch {
                        /* ignore */
                    }
                    emit();
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        emit();
                        onSubmit?.();
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        onCancel?.();
                    }
                }}
            />
        </div>
    );
}
